import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/mini/stores/auth'
import { BACKEND_ORIGIN } from '@/config/api'
import { useMessage } from '@/mini/composables/useMessage'
import {
  composeCardId,
  normalizeSetType,
  getCardApiBaseBySetType,
  applyFetchedCardDataToForm,
} from '@/utils/cardFormUtils'

export function useCardForm(formRef, { preserveApiIds = false } = {}) {
  const authStore = useAuthStore()
  const router = useRouter()
  const { showSuccessMessage, showDangerMessage, showInfoMessage } = useMessage()

  async function authenticatedApiFetch(path, options = {}) {
    try {
      const url = path.startsWith('http') ? path : `${BACKEND_ORIGIN}${path}`
      const response = await authStore.authFetch(url, options)
      if (response.status === 401) {
        authStore.logout()
        router.replace({ name: 'login' })
        throw new Error('Session expired')
      }
      return response
    } catch (error) {
      if (error instanceof Error && error.message === 'Not authenticated') {
        router.replace({ name: 'login' })
      }
      throw error
    }
  }

  const composedCardId = computed(() => composeCardId(formRef.set_type, formRef.set_id, formRef.card_number))

  const isFetchingData = ref(false)
  const fetchCandidates = ref([])
  const selectedFetchIndex = ref(0)

  async function fetchCardData() {
    if (isFetchingData.value) return
    fetchCandidates.value = []
    selectedFetchIndex.value = 0
    const cardId = composedCardId.value
    if (!cardId) {
      showDangerMessage('Set type and Card number are required to fetch data.')
      return
    }
    const setType = normalizeSetType(formRef.set_type, cardId)
    const base = getCardApiBaseBySetType(setType)
    if (!base) {
      showDangerMessage(`Unknown set type: ${setType || 'empty'}`)
      return
    }
    isFetchingData.value = true
    try {
      const response = await fetch(`https://www.optcgapi.com/api/${base}/card/${encodeURIComponent(cardId)}/`)
      if (!response.ok) throw new Error('Unable to fetch card data')
      const data = await response.json()
      const candidates = (Array.isArray(data) ? data : [data]).filter(item => item && typeof item === 'object')
      if (!candidates.length) throw new Error('Card not found in external API')
      if (candidates.length > 1) {
        fetchCandidates.value = candidates
        selectedFetchIndex.value = 0
        showInfoMessage('Multiple cards found. Select one and import it.')
        return
      }
      applyFetchedCardDataToForm(candidates[0], formRef, setType, preserveApiIds ? { preserveApiIds: true } : undefined)
      showSuccessMessage('Card data loaded. Review and save when ready.')
    } catch (err) {
      showDangerMessage(err instanceof Error ? err.message : 'Unable to fetch card data')
    } finally {
      isFetchingData.value = false
    }
  }

  function importSelected() {
    const selected = fetchCandidates.value[selectedFetchIndex.value]
    if (!selected) {
      showDangerMessage('Please select a valid card to import.')
      return
    }
    applyFetchedCardDataToForm(
      selected,
      formRef,
      normalizeSetType(formRef.set_type, composedCardId.value),
      preserveApiIds ? { preserveApiIds: true } : undefined,
    )
    showSuccessMessage('Selected card data loaded. Review and save when ready.')
  }

  return {
    authenticatedApiFetch,
    composedCardId,
    isFetchingData,
    fetchCandidates,
    selectedFetchIndex,
    fetchCardData,
    importSelected,
  }
}
